import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap, useUniqueId, ariaAttributes } from '../../utils/accessibility';
import { useAccessibility } from '../../contexts/AccessibilityContext';

interface AccessibleModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement>;
  finalFocusRef?: React.RefObject<HTMLElement>;
  className?: string;
}

/**
 * Accessible modal component with proper focus management, ARIA attributes,
 * and keyboard navigation support
 */
export const AccessibleModal: React.FC<AccessibleModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  initialFocusRef,
  finalFocusRef,
  className = '',
}) => {
  const { announce } = useAccessibility();
  const titleId = useUniqueId('modal-title');
  const descriptionId = useUniqueId('modal-description');
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  
  // Focus trap for the modal
  const focusTrapRef = useFocusTrap(
    isOpen,
    closeOnEscape ? onClose : undefined,
    initialFocusRef || closeButtonRef
  );
  
  // Store the element that was focused before the modal opened
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);
  
  // Memoize announce calls to prevent infinite re-renders
  const announceOpen = useCallback(() => {
    announce(`${title} dialog opened`, true);
  }, [announce, title]);
  
  const announceClose = useCallback(() => {
    announce(`${title} dialog closed`);
  }, [announce, title]);
  
  useEffect(() => {
    if (isOpen) {
      // Store the previously focused element
      previouslyFocusedElement.current = document.activeElement as HTMLElement;
      
      // Announce modal opening to screen readers
      announceOpen();
      
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore body scroll
        document.body.style.overflow = '';
        
        // Restore focus to the previously focused element
        if (finalFocusRef?.current) {
          finalFocusRef.current.focus();
        } else if (previouslyFocusedElement.current) {
          previouslyFocusedElement.current.focus();
        }
        
        // Announce modal closing
        announceClose();
      };
    }
  }, [isOpen, announceOpen, announceClose, finalFocusRef]);
  
  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };
  
  // Size classes
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
  };
  
  if (!isOpen) {
    return null;
  }
  
  const modalContent = (
    <div
      className="accessible-modal-overlay"
      onClick={handleOverlayClick}
      aria-hidden="true"
    >
      <div className="accessible-modal">
        <div
          ref={focusTrapRef}
          className={`accessible-modal-content ${sizeClasses[size]} ${className}`}
          {...ariaAttributes.dialog(titleId, description ? descriptionId : undefined)}
        >
          {/* Modal header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2
              id={titleId}
              className="text-lg font-semibold text-gray-900"
            >
              {title}
            </h2>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus-visible-enhanced"
              aria-label="Close dialog"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          
          {/* Modal body */}
          <div className="p-6">
            {description && (
              <p
                id={descriptionId}
                className="text-sm text-gray-600 mb-4"
              >
                {description}
              </p>
            )}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
  
  // Render modal in a portal to ensure it's at the top level
  return createPortal(modalContent, document.body);
};

/**
 * Confirmation modal with accessible design
 */
interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'info',
}) => {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };
  
  const variantStyles = {
    danger: 'btn-danger',
    warning: 'bg-yellow-600 text-white hover:bg-yellow-700 focus-visible:ring-yellow-500',
    info: 'btn-primary',
  };
  
  return (
    <AccessibleModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={message}
      initialFocusRef={confirmButtonRef}
      size="sm"
    >
      <div className="flex space-x-3 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="btn btn-secondary focus-visible-enhanced"
        >
          {cancelText}
        </button>
        <button
          ref={confirmButtonRef}
          type="button"
          onClick={handleConfirm}
          className={`btn ${variantStyles[variant]} focus-visible-enhanced`}
        >
          {confirmText}
        </button>
      </div>
    </AccessibleModal>
  );
};